/**
 * In-game pause button (sc-3290 phase 1).
 *
 * Fixed-position DOM button anchored to the bottom-right of the viewport.
 * Tap fires the same `requestPause` callback that the cabinet's Escape
 * listener uses, so touch users have the same pause-and-quit path
 * keyboard users do.
 *
 * Why fixed-position and not in `.control-panel`: the cabinet collapses
 * the footer to 0fr + `visibility: hidden` during `playing` mode, so a
 * DOM button parented to `.control-panel` is invisible exactly when we
 * need it. Living at `position: fixed` outside the cabinet grid is
 * immune to the collapse, same pattern the install banner uses.
 *
 * Bottom-right placement is the least-bad spot: top corners host SCORE/
 * SPEED HUDs in most cartridges; bottom-center is owned by the install
 * banner; bottom-right is the cleanest empty area across the catalog.
 *
 * Lifecycle:
 *   - `attachPauseButton({ onRequest })` once when a cartridge starts.
 *     Shows the button + wires its click handler.
 *   - `setPauseButtonVisible(false)` when the cartridge is sitting on a
 *     pre-game screen (press-start) or the pause overlay itself — taps
 *     while paused would queue a re-pause on resume.
 *   - `setPauseButtonVisible(true)` when active gameplay resumes.
 *   - `detachPauseButton()` when the cartridge teardown completes.
 */

interface PauseButtonOpts {
  /** Called when the player taps the pause button. */
  onRequest: () => void;
}

let state: {
  onRequest: () => void;
  clickListener: (e: MouseEvent) => void;
  el: HTMLButtonElement;
  hint: HTMLDivElement;
} | null = null;

const BUTTON_ID = 'pause-button';
const HINT_ID = 'pause-hint';

function ensureElement(): HTMLButtonElement {
  let el = document.getElementById(BUTTON_ID) as HTMLButtonElement | null;
  if (el) {
    // Re-parent if a previous version landed in the wrong container.
    if (el.parentElement !== document.body) {
      document.body.appendChild(el);
    }
    return el;
  }
  el = document.createElement('button');
  el.id = BUTTON_ID;
  el.type = 'button';
  el.className = 'pause-button';
  el.setAttribute('aria-label', 'Pause');
  el.hidden = true;
  // Two vertical bars — universal pause glyph. Built with inner spans so
  // the bars can be styled independently of any font.
  const bar1 = document.createElement('span');
  bar1.className = 'pause-button__bar';
  const bar2 = document.createElement('span');
  bar2.className = 'pause-button__bar';
  el.appendChild(bar1);
  el.appendChild(bar2);
  // Parent to body, not .control-panel — the footer is collapsed +
  // visibility:hidden during playing mode. body keeps the button alive.
  document.body.appendChild(el);
  return el;
}

function ensureHint(): HTMLDivElement {
  let hint = document.getElementById(HINT_ID) as HTMLDivElement | null;
  if (hint) {
    if (hint.parentElement !== document.body) {
      document.body.appendChild(hint);
    }
    return hint;
  }
  hint = document.createElement('div');
  hint.id = HINT_ID;
  hint.className = 'pause-hint';
  hint.setAttribute('aria-hidden', 'true');
  hint.hidden = true;
  hint.textContent = 'PAUSE FOR CONTROLS';
  document.body.appendChild(hint);
  return hint;
}

export function attachPauseButton(opts: PauseButtonOpts): void {
  detachPauseButton();
  const el = ensureElement();
  const hint = ensureHint();
  const clickListener = (evt: MouseEvent): void => {
    evt.preventDefault();
    state?.onRequest();
  };
  el.addEventListener('click', clickListener);
  el.hidden = false;
  hint.hidden = false;
  state = { onRequest: opts.onRequest, clickListener, el, hint };
}

export function detachPauseButton(): void {
  if (!state) return;
  state.el.removeEventListener('click', state.clickListener);
  state.el.hidden = true;
  state.hint.hidden = true;
  state = null;
}

/**
 * Show / hide the button + hint without unwiring the click handler.
 * Use during non-gameplay screens (press-start gate, pause overlay) so
 * the player can't queue a re-pause while already paused.
 */
export function setPauseButtonVisible(visible: boolean): void {
  if (!state) return;
  state.el.hidden = !visible;
  state.hint.hidden = !visible;
}
